import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getArticles } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import { StaggerGroup, StaggerItem } from "@/components/motion/Reveal";

export default function ResearchResources() {
  const { data: articles = [] } = useQuery({ queryKey: ["articles"], queryFn: getArticles });

  useSEO({
    title: "Research Resources | Renova Peptide Co.",
    description:
      "Educational reading on product documentation, laboratory storage considerations, and interpreting Certificates of Analysis — written for the research context.",
    path: "/research-resources",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Research Resources", path: "/research-resources" }]),
  });

  return (
    <>
      <PageHeader
        overline="Learn"
        title="Research Resources"
        intro="Educational reading on documentation, handling, and interpreting analytical records. Content is educational only and provides no guidance for human use."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Research Resources", path: "/research-resources" }]}
      />
      <section className="container-rnv py-16 md:py-24">
        <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((a) => (
            <StaggerItem key={a.slug}>
              <Link
                to={`/research-resources/${a.slug}`}
                className="group flex flex-col h-full bg-white rounded-sm border border-transparent hover:border-[#C8A96A]/50 transition-colors duration-500 p-8"
                data-testid={`resource-card-${a.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="overline">{a.category}</span>
                  <span className="text-xs text-[#85827D]">{a.read_time}</span>
                </div>
                <h2 className="font-serif text-2xl text-[#1E2A44] mt-5 leading-snug flex-1">{a.title}</h2>
                <p className="mt-3 text-sm text-[#85827D] leading-relaxed">{a.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#1E2A44] font-medium">
                  Read article
                  <ArrowUpRight size={15} className="text-[#C8A96A] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>
    </>
  );
}
